import React, { useEffect, useState } from 'react';
import { makeStyles } from 'design-system';
import {
  RankingOrderField,
  TextCollectionRankingOrderOption,
} from '@scale/shared/types/params/textcollection';
import { RankingOrderFieldAnnotation } from '@scale/shared/types/response/textcollection';
/* eslint-disable restrict-imports/restrict-imports */
import { Divider } from '@material-ui/core';
import LabeledOrPlaceholderItems from './LabeledOrPlaceholderItems';
import UnlabeledItems, { DEFAULT_OPTION_LABEL } from './UnlabeledItems';
import { useRankingOrderData } from './useRankingOrderData';
import ReactTooltip from 'react-tooltip';
import Markdown from '@scale/frontend-lib/markdown';
import { useLLMHelper } from 'react-scale/dashboard.scale.com/contexts/LLMHelperContext';

interface Props {
  rankingOrderOptions?: TextCollectionRankingOrderOption[];
  field: RankingOrderField;
  annotation: RankingOrderFieldAnnotation;
  setAnnotation: (annotation: RankingOrderFieldAnnotation) => void;
  disabled?: boolean;
  hideTitle?: boolean;
}

const RankingOrderFieldSection: React.FC<Props> = props => {
  const classes = useStyles();
  const { field, annotation, setAnnotation, disabled, rankingOrderOptions, hideTitle } = props;
  const { title, hint, first_label: firstLabel, last_label: lastLabel } = field;

  const numOptions = field.num_options || rankingOrderOptions?.length || 0;
  const numPositions = field.num_items_to_rank || rankingOrderOptions?.length || 0;
  const optionLabel = field.option_label || DEFAULT_OPTION_LABEL;
  const maxTiedItems = field.max_tied_items ?? 1;
  if (maxTiedItems <= 0 || maxTiedItems > 3)
    throw new Error('Invalid maxTiedItems. Must be within the range of [1, 3].');
  const itemsToRankCandidates = field?.items_to_rank_candidates;
  const isReviewMode = !!field?.is_review_mode;
  const {
    unlabeledItems,
    labeledOrPlaceholderItems,
    onDropToLabeledOrPlaceholderItem,
    onDeleteTiedItems,
  } = useRankingOrderData(
    rankingOrderOptions || [],
    numOptions,
    numPositions,
    maxTiedItems,
    optionLabel,
    annotation,
    setAnnotation,
    itemsToRankCandidates,
    isReviewMode,
  );

  const llmHelper = useLLMHelper();
  const [aiAssistant, setAiAssistant] = useState<string>('');
  const [aiAssistantLoading, setAiAssistantLoading] = useState<boolean>(false);
  useEffect(() => {
    if (
      llmHelper.aiAssistantEnabled &&
      Object.keys(llmHelper.aiAssistantPrompts).includes(props.field.field_id)
    ) {
      (async () => {
        try {
          setAiAssistantLoading(true);
          const content = await llmHelper.getAiAssistantResponse(props.field.field_id);
          if (content) setAiAssistant(content);
        } finally {
          setAiAssistantLoading(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [llmHelper.aiAssistantEnabled, llmHelper.aiAssistantPrompts, props.field.field_id]);

  return (
    <>
      {!hideTitle && <div className={classes.text2}>{title}</div>}
      {(!!aiAssistant.length || aiAssistantLoading) && (
        <div className={classes.aiAssistant}>
          {aiAssistantLoading && !aiAssistant.length
            ? 'AI assistant evaluation is loading...'
            : `The AI assistant suggests: ${aiAssistant}`}
        </div>
      )}
      <div className={classes.container}>
        <div className={classes.text1}>
          <span>Items to be ranked</span>
          {disabled && <span style={{ margin: '4px' }}>(Disabled)</span>}
          {hint && (
            <>
              <div
                className={classes.tooltipInfoIcon}
                data-tip
                data-for={`hint-tooltip-${field.field_id}`}
              >
                <InfoIcon />
              </div>
              <ReactTooltip id={`hint-tooltip-${field.field_id}`} place="top">
                <div
                  className={classes.tooltipText}
                  dangerouslySetInnerHTML={{ __html: Markdown.renderInline(field.hint ?? '') }}
                />
              </ReactTooltip>
            </>
          )}
        </div>
        <div className={classes.text2}>
          Drag items from this section into the ranking spots below
        </div>

        {/* Unlabeled Section */}
        <UnlabeledItems unlabeledItems={unlabeledItems} disabled={disabled} />

        <Divider />

        <div
          className={classes.text3}
        >{`1 = "${firstLabel}" and ${numPositions} = "${lastLabel}"`}</div>

        {/* Labeled Section */}
        <LabeledOrPlaceholderItems
          labeledOrPlaceholderItems={labeledOrPlaceholderItems}
          onDropToLabeledOrPlaceholderItem={onDropToLabeledOrPlaceholderItem}
          onDeleteTiedItems={onDeleteTiedItems}
          disabled={disabled}
        />
      </div>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.colors.gray10}`,
    borderRadius: 4,
    boxShadow: '0px 0px 4px rgba(17, 24, 39, 0.1)',
    background: theme.colors.white,
  },
  text1: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
  },
  text2: {
    marginBottom: theme.spacing(1),
    display: 'inline-block',
    whiteSpace: 'pre-wrap',
  },
  text3: {
    fontSize: '10px',
    margin: `${theme.spacing(1)}px 0px`,
  },
  unlabeledColumn: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(1),
  },
  labeledColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  unlabeledItem: {
    background: theme.colors.white,
    borderRadius: '10px',
    height: '40px',
    border: `1px solid ${theme.colors.gray20}`,
    overflow: 'hidden',
    margin: '4px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '6px',
  },
  labeledItemContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '6px',
  },
  labeledItem: {
    background: theme.colors.white,
    borderRadius: '10px',
    height: '40px',
    border: `1px solid ${theme.colors.gray20}`,
    overflow: 'hidden',
    margin: '4px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '6px',
  },
  placeholderItem: {
    background: theme.colors.gray01,
    borderRadius: '10px',
    height: '40px',
    border: `1px solid ${theme.colors.gray20}`,
    overflow: 'hidden',
    margin: '4px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '6px',
  },
  deleteIcon: {
    cursor: 'pointer',
  },
  tooltipInfoIcon: {
    paddingLeft: 8,
    paddingRight: 8,
    color: 'rgba(1, 1, 1, 0.75)',
  },
  tooltipText: {
    maxWidth: 496,
  },
  aiAssistant: {
    marginBottom: 8,
    padding: 8,
    whiteSpace: 'pre-wrap',
    backgroundColor: 'rgba(144, 202, 249, .2)',
    borderRadius: 8,
  },
}));

const InfoIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="currentColor"
      d="M6.9998 0.799805C3.57588 0.799805 0.799805 3.57688 0.799805 6.9998C0.799805 10.4247 3.57588 13.1998 6.9998 13.1998C10.4237 13.1998 13.1998 10.4247 13.1998 6.9998C13.1998 3.57688 10.4237 0.799805 6.9998 0.799805ZM6.9998 3.5498C7.5797 3.5498 8.0498 4.0199 8.0498 4.5998C8.0498 5.1797 7.5797 5.6498 6.9998 5.6498C6.4199 5.6498 5.9498 5.1797 5.9498 4.5998C5.9498 4.0199 6.4199 3.5498 6.9998 3.5498ZM8.3998 9.8998C8.3998 10.0655 8.26548 10.1998 8.0998 10.1998H5.8998C5.73413 10.1998 5.5998 10.0655 5.5998 9.8998V9.2998C5.5998 9.13413 5.73413 8.9998 5.8998 8.9998H6.1998V7.3998H5.8998C5.73413 7.3998 5.5998 7.26548 5.5998 7.0998V6.4998C5.5998 6.33413 5.73413 6.1998 5.8998 6.1998H7.4998C7.66548 6.1998 7.7998 6.33413 7.7998 6.4998V8.9998H8.0998C8.26548 8.9998 8.3998 9.13413 8.3998 9.2998V9.8998Z"
    />
  </svg>
);

export default RankingOrderFieldSection;
